import { Controller, type Control } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { WeddingQuoteInput } from "@/lib/plan-wizard-schema";

// Trimmed to the most common picks (#227) — 13 chips caused decision
// paralysis on mobile. Anything else goes through "Other" + free text.
const EVENT_TYPES = [
  "Birthday Party",
  "Baby Shower",
  "Bridal Shower",
  "Corporate Event",
  "Celebration of Life",
  "Other",
] as const;

/** Live-service event type chips (extracted from WeddingQuoteBranch, #1221). */
export function EventTypeChips({ control, error }: { control: Control<WeddingQuoteInput>; error?: string }) {
  return (
    <div className="grid gap-2">
      <Label id="w-event-type-label">Event type</Label>
      <Controller
        control={control}
        name="event_type_name"
        render={({ field }) => {
          const isOther = field.value !== "" && !EVENT_TYPES.slice(0, -1).includes(field.value as typeof EVENT_TYPES[number]);
          return (
            <div className="space-y-2">
              <div
                className="flex flex-wrap gap-2"
                role="radiogroup"
                aria-labelledby="w-event-type-label"
                aria-invalid={!!error}
                aria-describedby={error ? "w-event-type-error" : undefined}
              >
                {EVENT_TYPES.map((t) => {
                  const selected = t === "Other" ? isOther : field.value === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        if (t === "Other") {
                          field.onChange(isOther ? "" : " ");
                        } else {
                          field.onChange(selected ? "" : t);
                        }
                      }}
                      className={`inline-flex min-h-[40px] items-center rounded-full border px-4 py-1.5 text-sm transition-colors ${
                        selected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background hover:border-primary/40"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              {isOther && (
                <Input
                  placeholder="What kind of event?"
                  aria-describedby={error ? "w-event-type-error" : undefined}
                  value={field.value?.trim() || ""}
                  onChange={(e) => field.onChange(e.target.value || " ")}
                  autoFocus
                />
              )}
            </div>
          );
        }}
      />
      {error && (
        <p id="w-event-type-error" role="alert" className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
